<script setup>
  import { ref, computed, watchEffect, onMounted } from 'vue';
  import { isToday, isTomorrow, isYesterday, isPast } from 'date-fns';
  import ProForecastSpotForecast from '@/components/ProForecastSpotForecast.vue';
  import ProForecastCredits from '@/components/ProForecastCredits.vue';
  import SegmentedControl from "@/components/controls/SegmentedControl.vue";
  import SegmentedControlButton from "@/components/controls/SegmentedControlButton.vue";
  import ToggleList from "@/components/controls/ToggleList.vue";   
  import ToggleItem from "@/components/controls/ToggleItem.vue";   
  import Popup from '@/components/controls/Popup.vue';
  import ModalProforecastHelp from './ModalProforecastHelp.vue';
  import Icon from '@/components/icons/Icon.vue';
  import ControlButton from './controls/ControlButton.vue';

  const emit = defineEmits(['accordian:toggle', 'state:change', 'settings:change']);

  const props = defineProps({
    spotForecasts: {
      type: Array,
      required: true
    },
    forecaster: {
      type: Object,
      required: true
    },
    showDailyForecast: {
      type: Boolean,
      default: false
    },
    validDate: {
      type: String,
      required: true
    },
    isSample: {
      type: Boolean,
      default: false
    },
    spotForecastSettings: {
      type: Object,
      default: () => {}
    },
    spotForecastState: {
      type: Object,
      required: false
    },
    spotForecastStateLoading: {
      type: Boolean,
      default: false
    }
  });

  const getSpotForecastState = (spotId) => {
    return props.spotForecastState ? props.spotForecastState[`${spotId}`] : null;
  };

  const showPopup = ref(false);
  const showHint = ref(false);
  const showDirectionArrow = ref(props.spotForecastSettings.showDirectionArrow)
  const showDirectionValue = ref(props.spotForecastSettings.showDirectionValue)
  const directionValueType = ref(props.spotForecastSettings.directionValueType)
  const showProForecastHelpModal = ref(false);

  const blockDateHeader = computed(() => {
    if (props.isSample) return "Today's Forecast";

    const date = new Date(props.validDate);

    if (isToday(date)) {
      return "Today's Spot Forecasts";
    } else if (isTomorrow(date)) {
      return "Tomorrow's Spot Forecasts";
    } else if (isYesterday(date)) {
      return "Yesterdays's Spot Forecasts";
    } else if (isPast(date)) {
      return "Previous Spot Forecasts";
    } else {
      return "Upcoming Spot Forecasts";
    }
  });
  
  const handleAccordianToggle = (e) => {
    if (props.isSample) return;
    emit('accordian:toggle', e);
    const { collapsed, spotId } = e;

    const currentSpotForecastState = Object.hasOwnProperty.call(props.spotForecastState, spotId) ? props.spotForecastState[spotId] : {};

    const spotState = {
      ...props.spotForecastState,
      [spotId]: {
        ...currentSpotForecastState,
        collapsed: collapsed
      }
    }

    emit('state:change', spotState)
  }


  const handleDirectionArrowChange = (e) => {
    const settings = {
      ...props.spotForecastSettings,
      showDirectionArrow: showDirectionArrow.value
    }
    emit('settings:change', settings)
  }

  const handleDirectionValueChange = (e) => {
    const settings = {
      ...props.spotForecastSettings,
      showDirectionValue: showDirectionValue.value
    }
    emit('settings:change', settings)
  }

  const handleDirectionValueTypeChange = (e) => {
    const settings = {
      ...props.spotForecastSettings,
      directionValueType: directionValueType.value
    }
    emit('settings:change', settings)
  }

  watchEffect(() => {
    showDirectionArrow.value = props.spotForecastSettings.showDirectionArrow
    showDirectionValue.value = props.spotForecastSettings.showDirectionValue
    directionValueType.value = props.spotForecastSettings.directionValueType  
  })
</script>

<template>
  <section v-if="!props.spotForecastStateLoading" class="jwx-page__section" data-divider="true">
    <div class="jwx-page__section-header" data-has-actions="true">
      <h2 class="jwx-page__section-title">{{ blockDateHeader }}</h2>
      <p class="jwx-page__section__header-info jwx-pro-forecast__units">Wind speed: kts</p>
      <div class="jwx-page__section-header-actions">
        <Popup
          id="spot-forecast-settings-menu"
          icon="more-menu"
          :showPopup="showPopup"
          class="jwx-page__section-header-action"
        >
          <template #button-content>
          <div class="jwx-popup__button">
            <Icon name="more-menu" class="jwx-user-favorite__menu-icon" />
            <span class='sr-only'>Open Spot Forecast Settings Menu</span>
          </div>
          </template>
          <template #menu-content>
            <div class="jwx-popup-menu">            
              <div class="jwx-action-menu">
                <ToggleList>
                  <ToggleItem
                    @change="handleDirectionArrowChange"
                    :value="props.spotForecastSettings.showDirectionArrow"
                    iconName="station-marker"
                    id="settings-show-dir-arrow"
                    v-model.number="showDirectionArrow"
                  >
                    Show Direction Arrows
                  </ToggleItem>                   
                  <ToggleItem
                    @change="handleDirectionValueChange"
                    :value="props.spotForecastSettings.showDirectionValue"
                    iconName="compass"
                    id="settings-show-dir-value"
                    v-model.number="showDirectionValue"
                  >
                    Show Direction Value
                  </ToggleItem>   
                  <SegmentedControl
                    @click="handleDirectionValueTypeChange"
                    :value="props.spotForecastSettings.directionValueType"
                    :disabled="props.spotForecastSettings.showDirectionValue === false"
                    v-model="directionValueType"
                    label="Select the time period of the onsite reports to show."
                  >
                    <SegmentedControlButton value="cardinal">Cardinal</SegmentedControlButton>
                    <SegmentedControlButton value="degrees">Degrees</SegmentedControlButton>
                  </SegmentedControl>                
                </ToggleList>
              </div>
            </div>
          </template>
        </Popup>
        <div class="jwx-page__section-header-action">
          <ControlButton
            @click.prevent="showProForecastHelpModal = !showProForecastHelpModal"
            data-surface="1"
            data-shape="circle"
            style="--jwx-button-control-min-width: 1.5rem; --jwx-button-control-icon-max-size: .875rem; --jwx-button-control-text-color: var(--jwx-text-color-subtle)"
          >
            <Icon name="question-mark" class="jwx-button-control__icon" />
          </ControlButton>     
        </div>
      </div>
      <ProForecastCredits 
        v-if="props.showDailyForecast" 
        :forecaster="props.forecaster" 
      />
    </div>
    <ProForecastSpotForecast
      v-for="spotForecast in props.spotForecasts"
      @accordian:toggle="handleAccordianToggle($event)"
      :spotForecast="spotForecast"
      :spotForecastState="getSpotForecastState(spotForecast.spot_id)"
      :spotForecastSettings="props.spotForecastSettings"
      :key="spotForecast.spot_id"
    >
    </ProForecastSpotForecast>
  </section>
  <ModalProforecastHelp 
    v-if="showProForecastHelpModal"
    :show="showProForecastHelpModal"
    @close="showProForecastHelpModal = false"
  />
</template>